import { create } from 'zustand';
import staffService from '../api/staffService';
import logger from '../utils/logger';

const buildIdentityKey = (member = {}) => {
  const name = (member.name || '').trim().toLowerCase();
  const designation = (member.designation || '').trim().toLowerCase();
  const department = (member.department || '').trim().toLowerCase();
  const semanticKey = `${name}|${designation}|${department}`;

  if (semanticKey !== '||') return `identity:${semanticKey}`;
  if (member._id) return `id:${member._id}`;
  return `fallback:${JSON.stringify(member)}`;
};

const dedupeStaffRecords = (records = []) => {
  const map = new Map();
  records.forEach((member) => {
    const key = buildIdentityKey(member);
    if (!map.has(key)) {
      map.set(key, member);
    }
  });
  return Array.from(map.values());
};

const buildFormData = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (key === 'image' && value instanceof File) {
      formData.append('image', value);
    } else if ((key === 'subjects' || key === 'achievements') && Array.isArray(value)) {
      formData.append(key, value.join(','));
    } else if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });
  return formData;
};

// ponytail: Zustand store replacing the StaffContext boilerplate
export const useStaffStore = create((set, get) => ({
  staff: [],
  stats: null,
  loading: true,
  error: null,

  fetchStaff: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await staffService.getStaff(params);
      if (response.status === 'success' && response.data?.staff) {
        set({ staff: dedupeStaffRecords(response.data.staff) });
      } else {
        set({ staff: [] });
      }
    } catch (err) {
      logger.error('Error fetching staff:', err);
      set({ error: err.response?.data?.message || 'Failed to load staff directory', staff: [] });
    } finally {
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const response = await staffService.getStaffStats();
      if (response.status === 'success' && response.data) {
        set({ stats: response.data });
      }
    } catch (err) {
      logger.error('Error fetching staff stats:', err);
      set({ stats: null });
    }
  },

  addStaffMember: async (payload) => {
    const formData = buildFormData(payload);
    const response = await staffService.createStaffMember(formData);

    if (response.status === 'success' && response.data?.staff) {
      set((state) => ({ staff: dedupeStaffRecords([...state.staff, response.data.staff]) }));
      return response.data.staff;
    }
    throw new Error(response.message || 'Failed to create staff member');
  },

  updateStaffMember: async (id, payload) => {
    const formData = buildFormData(payload);
    const response = await staffService.updateStaffMember(id, formData);

    if (response.status === 'success' && response.data?.staff) {
      set((state) => ({
        staff: dedupeStaffRecords(state.staff.map((m) => (m._id === id ? response.data.staff : m))),
      }));
      return response.data.staff;
    }
    throw new Error(response.message || 'Failed to update staff member');
  },

  deleteStaffMember: async (id) => {
    await staffService.deleteStaffMember(id);
    set((state) => ({ staff: state.staff.filter((m) => m._id !== id) }));
  },

  bulkDeleteStaffMembers: async (ids) => {
    await staffService.bulkDeleteStaff(ids);
    set((state) => ({ staff: state.staff.filter((m) => !ids.includes(m._id)) }));
  },

  getActiveStaff: () => get().staff.filter((m) => m.isActive),
  getStaffByDepartment: (dept) => get().staff.filter((m) => m.department === dept && m.isActive),
}));

// ponytail: init driven by PublicDataLoader — not here

